import { TFileToUpload } from '~/types/file-upload.types';
import { IImage } from '~/types/model.types';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { trackError } from '~/services/logger/error.utils';
import { ToastStore } from '~/stores/toast.store';
import { ImageService } from '~/services/image.service';
import { UserService } from '~/services/user.service';
import { CompanyStore } from '~/stores/company.store';
import { IUserGetter } from '~/stores/base-user.store';
import { TImageUploadType } from '~/types/server.types';
import { CompanyService } from '~/services/company.service';

interface IProps {
    companyStore: CompanyStore;
    userStore: IUserGetter;
    userService: UserService;
    imageService: ImageService;
    toastStore: ToastStore;
    companyService: CompanyService;
}

export class BaseImageController {
    protected companyStore: CompanyStore;
    protected userStore: IUserGetter;
    protected userService: UserService;
    protected imageService: ImageService;
    protected toastStore: ToastStore;
    protected companyService: CompanyService;

    constructor({
        companyStore,
        userStore,
        userService,
        imageService,
        toastStore,
        companyService
    }: IProps) {
        this.companyStore = companyStore;
        this.userStore = userStore;
        this.userService = userService;
        this.imageService = imageService;
        this.toastStore = toastStore;
        this.companyService = companyService;
    }

    async uploadImageOnly({
        file,
        folder,
        setUploadProgress,
        shouldShowErrorToast = true
    }: {
        file: TFileToUpload | string;
        folder?: string;
        setUploadProgress?: (
            progress: number,
            file: TFileToUpload | string
        ) => void;
        shouldShowErrorToast?: boolean;
    }): Promise<IImage> {
        try {
            const image = await this.imageService.uploadImage({
                file,
                folder,
                setUploadProgress
            });

            if (typeof file !== 'string') {
                image.internalId = file._id;
            }

            setUploadProgress?.(100, file);

            return image;
        } catch (error) {
            if (typeof file !== 'string') {
                delete file.dataUrl;
            }
            trackError({
                error,
                message:
                    '@image.controller.ts: error while trying to upload to cloudinary',
                context: { file }
            });
            if (shouldShowErrorToast) {
                this.toastStore.add({
                    type: ToastType.Error,
                    text: 'Error occurred while uploading image'
                });
            }
            return Promise.reject(error);
        }
    }

    async uploadImage({
        file,
        setUploadProgress,
        uploadType,
        shouldShowErrorToast = true,
        userId
    }: {
        file: TFileToUpload | string;
        setUploadProgress?: (
            progress: number,
            file: TFileToUpload | string
        ) => void;
        uploadType: TImageUploadType;
        shouldShowErrorToast?: boolean;
        userId?: string;
    }) {
        const companyId = this.companyStore.currCompany?._id;
        let image: IImage;
        let folder;

        switch (uploadType) {
            case 'profile_image':
                folder = `user/${userId}/profile_image`;
                break;

            case 'icon':
                folder = `company/${companyId}/icon`;
                break;

            default:
                break;
        }

        try {
            image = await this.imageService.uploadImage({
                file,
                folder,
                setUploadProgress
            });
        } catch (error) {
            if (typeof file !== 'string') {
                delete file.dataUrl;
            }
            trackError({
                error,
                message:
                    '@image.controller.ts: error while trying to upload to cloudinary',
                context: { companyId, file, uploadType }
            });
            if (shouldShowErrorToast) {
                this.toastStore.add({
                    type: ToastType.Error,
                    text: 'Error occurred while uploading image'
                });
            }
            return Promise.reject(error);
        }
        if (typeof file !== 'string') {
            image.internalId = file._id;
        }

        if (uploadType === 'profile_image') {
            return this.userService.updateUserProfilePicture(
                this.userStore.currUser?._id,
                image
            );
        }

        if (uploadType !== 'cover' && uploadType !== 'icon') {
            setUploadProgress?.(100, file);
            return image;
        }

        // handling 'cover' upload type (update to library):
        try {
            const uploadedFile = await this.companyService.addImageToServer({
                image,
                companyId,
                uploadType
            });
            setUploadProgress?.(100, file);
            if (typeof file !== 'string') {
                uploadedFile.internalId = file._id;
            }
            return uploadedFile;
        } catch (error) {
            trackError({
                error,
                message:
                    '@image.controller.ts: error while trying to update image in DB',
                context: { companyId, image, uploadType }
            });
            if (shouldShowErrorToast) {
                this.toastStore.add({
                    type: ToastType.Error,
                    text: 'Error occurred while adding image to library'
                });
            }
            return Promise.reject(error);
        }
    }
}
